import { useState } from 'react';

import { MoreVertical, Download, Loader2, Plus } from 'lucide-react';

import { useGetSandboxes } from '@/api/getSandboxes';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useGame } from '@/hooks/useGame';
import { useSandboxContext } from '@/providers';
import { useErrorHandler } from '@/services/errorHandler';

import { CreateSandboxModal } from './createSandboxModal';
import { useExportSandbox } from '../api/exportSandbox';

export const SandboxActionsMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { handleError, handleSuccess, handleInfo } = useErrorHandler();
  const game = useGame();
  const { selectedSandbox } = useSandboxContext();
  const { data: sandboxesData } = useGetSandboxes({ game_code: game.game_code });

  // Find the current sandbox to get its ID
  const currentSandbox = sandboxesData?.sandboxes.find(
    (sandbox) => sandbox.code === selectedSandbox
  );

  const exportMutation = useExportSandbox({
    mutationConfig: {
      onSuccess: () => {
        handleSuccess('Sandbox exported successfully');
        setIsOpen(false);
      },
      onError: handleError({
        statusHandlers: {
          404: 'Sandbox not found',
          403: 'You do not have permission to export this sandbox',
          500: 'Server error: Unable to export sandbox. Please try again later.',
        },
      }),
    },
  });

  const handleExport = () => {
    if (!currentSandbox) {
      handleInfo('No sandbox selected');
      return;
    }

    exportMutation.mutate({ sandboxId: currentSandbox.id });
  };

  const isLoading = exportMutation.isPending;

  return (
    <TooltipProvider>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm"
                className="h-9 w-9 p-0"
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <MoreVertical className="h-4 w-4" />
                )}
                <span className="sr-only">Open sandbox actions menu</span>
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Sandbox Actions</p>
          </TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem
            onClick={() => {
              setIsCreateModalOpen(true);
              setIsOpen(false);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            <div className="flex flex-col">
              <span>Create Sandbox</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleExport}
            disabled={isLoading || !currentSandbox}
          >
            <Download className="mr-2 h-4 w-4" />
            <div className="flex flex-col">
              <span>Export Sandbox</span>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <CreateSandboxModal 
        open={isCreateModalOpen} 
        onOpenChange={setIsCreateModalOpen}
      />
    </TooltipProvider>
  );
};

